/**
 * Custom Image Loader for Cloudflare R2 CDN
 *
 * This loader bypasses Next.js image optimization for CDN assets
 * since they're already optimized and cached by Cloudflare.
 */

interface ImageLoaderProps {
  src: string;
  width: number;
  quality?: number;
}

const CDN_URL = process.env.NEXT_PUBLIC_CDN_URL || 'https://assets.marketbetter.ai';

export default function cloudflareImageLoader({ src }: ImageLoaderProps): string {
  // Already an absolute CDN URL → serve directly from R2.
  if (src.startsWith('https://assets.marketbetter.ai')) {
    return src;
  }

  // Any other absolute URL → return as-is.
  if (src.startsWith('http://') || src.startsWith('https://')) {
    return src;
  }

  // Next.js internal assets (statically-imported images under /_next/static)
  // are emitted by the build and live on the app origin, not R2 — leave them be.
  if (src.startsWith('/_next/')) {
    return src;
  }

  // Every other local asset (`/images`, `/videos`, `/icons`, root SVGs, …) lives
  // on R2 — serve it from the CDN. The R2 bucket mirrors `public/`. Routing to the
  // app origin instead breaks under CSP: `img-src` allows 'self' (apex) and
  // assets.marketbetter.ai but NOT www, while NEXT_PUBLIC_HOST_URL points at www —
  // so origin-routed icons (e.g. /icons/facts_icons/hubspot.svg) were blocked by
  // the browser (net::ERR, status 0). Keeping all assets on the CDN avoids that.
  if (src.startsWith('/')) {
    return `${CDN_URL}${src}`;
  }

  return src;
}
